/**
 * Topic Service - Handles topic-related API calls
 */
import { apiClient, withErrorHandling } from '../api/index.js';

class TopicService {
  constructor() {
    this.apiClient = apiClient;
  }

  /**
   * Fetch topics for a specific course
   * @param {string} courseId - The ID of the course
   * @returns {Promise} Service response with topics data
   */
  async getTopicsByCourseId(courseId) {
    return await withErrorHandling(
      async () => {
        // Initialize the API client
        const client = await this.apiClient.initialize();

        // Make the API call to get active topics
        const endpoint = `/topics/getAll?courseId=${courseId}&status=ACTIVE`;
        const response = await client.get(endpoint);

        // Log the response for debugging
        console.log('Topics API response:', response);
        
        // Return the data in the expected format
        if (response.data && response.data.data) {
          return { data: response.data.data };
        }
        return response;
      },
      { operation: 'getTopicsByCourseId', courseId }
    );
  }

  /**
   * Fetch a specific topic by ID with resources
   * @param {string} topicId - The ID of the topic
   * @returns {Promise} Service response with topic data including resources
   */
  async getTopicById(topicId) {
    return await withErrorHandling(
      async () => {
        // Initialize the API client
        const client = await this.apiClient.initialize();

        // Make the API call to get topic by ID
        const endpoint = `/topics/${topicId}`;
        const response = await client.get(endpoint);

        console.log(`🔍 Topic API Response for ID ${topicId}:`, response.data);

        // Log resources information for debugging
        if (response.data && response.data.data && response.data.data.resources) {
          console.log(`📄 Found ${response.data.data.resources.length} resources for topic`);
          response.data.data.resources.forEach((resource, index) => {
            console.log(`  📄 Resource ${index + 1}:`, {
              title: resource.title,
              orderIndex: resource.orderIndex,
              hasContent: !!resource.content
            });
          });
        }

        return response.data;
      },
      { operation: 'getTopicById', topicId }
    );
  }

  /**
   * Fetch resources for a specific topic
   * @param {string} topicId - The ID of the topic
   * @returns {Promise} Service response with resources data
   */
  async getTopicResources(topicId) {
    return await withErrorHandling(
      async () => {
        // Initialize the API client
        const client = await this.apiClient.initialize();

        // Make the API call to get resources
        const endpoint = `/resources/getByTopicId/${topicId}`;
        const response = await client.get(endpoint);

        return response.data;
      },
      { operation: 'getTopicResources', topicId }
    );
  }
}

// Create a singleton instance
export const topicService = new TopicService();

export default topicService;